/*
 * album/shared.ts - the photo album's data (the #album_data JSON the server writes, already filtered by the
 * visitor's access), favorites (D14, localStorage, optional), the toast, and the tile builder that mirrors
 * Views/Gallery/_AlbumTile.cshtml for tiles made in the browser (chips, guest wall pages, the grid).
 */

export interface Photo {
  id: string;
  thumb: string;
  full: string;
  w: number;
  h: number;
  /** The thumbnail's pixel width (the srcset w), 0 when unknown - PhotoVM.Tw. */
  tw: number;
  caption: string | null;
  chapter: string | null;
  subgroup: number | null;
  takenAt: string | null;
  guest: boolean;
  by: string | null;
}

export interface Subgroup {
  id: number;
  name: string | null;
  note: string | null;
  time: string | null;
  total: number;
  /** Visible guest photos filed under this sub-group (Grid chip counts, AL6). */
  guests: number;
  picks: string[];
}

export interface Chapter {
  slug: string;
  name: string;
  auto: boolean;
  total: number;
  /** AlbumDisplay: 0 Portraits, 1 Timeline, 2 Highlight, 3 Trip. */
  display: number;
  guestCount: number;
  picks: string[];
  guests: string[];
  subgroups: Subgroup[];
}

export interface AlbumData {
  photos: Record<string, Photo>;
  chapters: Chapter[];
  guestWall: { ids: string[]; total: number; page: number };
  deep: { view: string; group: string | null; photo: string | null };
  hasPass: boolean;
  counts: { pro: number; guest: number };
  /** A one-shot notice (LINE login outcome, share code accepted), shown as a toast. */
  notice: string | null;
  /** Most photos in one favorites zip (GalleryController.ZipMax). */
  zipMax: number;
  i18n: Record<string, string>;
}

/** The automatic 「更多照片」 chapter (D8) - GalleryController.UngroupedKey. */
export const UNGROUPED = "_more";

export function loadData(): AlbumData | null {
  const el = document.getElementById("album_data");
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent) as AlbumData;
  } catch {
    return null;
  }
}

export function reduceMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** "{0}" placeholders, as in the resx strings. */
export function fmt(s: string | undefined, ...args: (string | number)[]): string {
  return (s ?? "").replace(/\{(\d+)\}/g, (_, i: string) => String(args[Number(i)] ?? ""));
}

export function photosOf(data: AlbumData, ids: string[]): Photo[] {
  return ids.map((id) => data.photos[id]).filter((p): p is Photo => !!p);
}

export function chapterOf(data: AlbumData, slug: string | null | undefined): Chapter | undefined {
  return data.chapters.find((c) => c.slug === slug);
}

/** A grid / guest-wall page from GET /Gallery/Photos (AL4). */
export async function fetchPhotos(q: { g?: string | null; sg?: number | null; src?: string; skip: number; take: number }): Promise<{ total: number; items: Photo[] }> {
  const p = new URLSearchParams();
  if (q.g) p.set("g", q.g);
  if (q.sg != null) p.set("sg", String(q.sg));
  if (q.src) p.set("src", q.src);
  p.set("skip", String(q.skip));
  p.set("take", String(q.take));
  const r = await fetch(`/Gallery/Photos?${p}`, { headers: { Accept: "application/json" }, credentials: "same-origin" });
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()) as { total: number; items: Photo[] };
}

/** The favorites sheet (AL6): these IDs as photos, in order - only the ones this visitor may see come back. */
export async function fetchByIds(ids: string[]): Promise<Photo[]> {
  if (!ids.length) return [];
  const p = new URLSearchParams({ ids: ids.join(",") });
  const r = await fetch(`/Gallery/Photos?${p}`, { headers: { Accept: "application/json" }, credentials: "same-origin" });
  if (!r.ok) throw new Error(String(r.status));
  return ((await r.json()) as { items: Photo[] }).items;
}

/** The D15 deep link to one photo: Grid, filtered to its chapter, viewer open on it. */
export function deepLink(p: Photo): string {
  const q = new URLSearchParams({ view: "grid", g: p.chapter ?? UNGROUPED, p: p.id });
  return `${location.origin}/Gallery?${q}`;
}

/** The guest wall's stable tilt (0-4) - the same rule as Tilt() in _GuestWall.cshtml. */
export function tilt(id: string): number {
  let n = 0;
  for (let i = 0; i < id.length; i++) n += id.charCodeAt(i);
  return n % 5;
}

/** Thumbnail + full file as a srcset (AL7) - AlbumImg.Srcset in GalleryController.Album.cs. */
export function srcsetOf(p: Photo): string | null {
  return p.tw > 0 && p.w > p.tw ? `${p.thumb} ${p.tw}w, ${p.full} ${p.w}w` : null;
}

/** The slot width per tile kind (its modifier class) - AlbumImg.Sizes in GalleryController.Album.cs, keep in step. */
export function sizesFor(mod: string): string {
  const has = (c: string): boolean => ` ${mod} `.includes(` ${c} `);
  if (has("album-tile--feature")) return "(min-width: 1400px) 1296px, 94vw";
  if (has("album-tile--big")) return "(min-width: 1400px) 864px, (min-width: 48rem) 64vw, 94vw";
  if (has("album-tile--moment")) return "(min-width: 1400px) 760px, (min-width: 48rem) 56vw, 86vw";
  if (has("album-tile--portrait")) return "(min-width: 1400px) 380px, (min-width: 48rem) 28vw, 46vw";
  if (has("album-tile--reel")) return "(min-width: 40.5rem) 17rem, 42vw";
  if (has("album-tile--thumb")) return "(min-width: 1400px) 250px, (min-width: 48rem) 19vw, 29vw";
  if (has("album-tile--polaroid")) return "(min-width: 32.5rem) 13rem, 40vw";
  if (has("album-tile--masonry")) return "(min-width: 1400px) 324px, (min-width: 75rem) 24vw, (min-width: 40rem) 31vw, 48vw";
  if (has("album-tile--grid")) return "(min-width: 1400px) 216px, (min-width: 75rem) 16vw, (min-width: 48rem) 24vw, 32vw";
  return "(min-width: 1400px) 432px, (min-width: 48rem) 31vw, 47vw";   // a mosaic 1x1
}

/** A tile like _AlbumTile.cshtml (AL7: with the same srcset / sizes). */
export function tile(p: Photo, mod = "", label?: string | null): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = `album-tile ${mod}`.trim();
  a.href = p.full;
  a.dataset.photo = p.id;
  const img = document.createElement("img");
  img.className = "album-tile__img";
  // lazy + sizes before the sources, so the browser picks with them in place
  img.loading = "lazy";
  img.decoding = "async";
  img.alt = p.caption ?? "";
  if (p.w > 0) img.width = p.w;
  if (p.h > 0) img.height = p.h;
  const set = srcsetOf(p);
  if (set) {
    img.sizes = sizesFor(mod);
    img.srcset = set;
  }
  img.src = p.thumb;
  a.appendChild(img);
  if (label && label.trim()) {
    const s = document.createElement("span");
    s.className = "album-tile__label";
    s.textContent = label;
    a.appendChild(s);
  }
  return a;
}

/**
 * A grid / favorites-sheet cell (AL6): the tile + a ♥ button beside it (a button can't sit inside the tile's link),
 * and a small camera badge on guest photos. The ♥ is handled by one delegated listener ({@link bindHearts}).
 */
export function cell(p: Photo, favs: Favorites, data: AlbumData, mod = "album-tile--grid"): HTMLElement {
  const d = document.createElement("div");
  d.className = "album-cell album-in";
  d.appendChild(tile(p, mod));
  if (p.guest) {
    const g = document.createElement("span");
    g.className = "album-cell__guest";
    g.title = data.i18n.guestPhoto ?? "";
    g.innerHTML = '<span class="fa-solid fa-camera" aria-hidden="true"></span>';
    d.appendChild(g);
  }
  const b = document.createElement("button");
  b.type = "button";
  b.className = "album-heart";
  b.dataset.favBtn = p.id;
  b.innerHTML = '<span aria-hidden="true"></span>';
  paintHeart(b, favs.has(p.id), data);
  d.appendChild(b);
  return d;
}

export function paintHeart(b: HTMLElement, on: boolean, data: AlbumData): void {
  b.classList.toggle("is-on", on);
  b.setAttribute("aria-pressed", on ? "true" : "false");
  b.setAttribute("aria-label", on ? data.i18n.favRemove ?? "" : data.i18n.fav ?? "");
  const icon = b.querySelector("span");
  if (icon) icon.className = `${on ? "fa-solid" : "fa-regular"} fa-heart`;
}

/** ♥ buttons inside `host`: toggle + beat + toast; every heart on the page follows the favorites. */
export function bindHearts(host: HTMLElement, favs: Favorites, data: AlbumData): void {
  host.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-fav-btn]");
    if (!b || !host.contains(b)) return;
    e.preventDefault();
    e.stopPropagation();
    const on = favs.toggle(b.dataset.favBtn!);
    b.classList.remove("is-beat");
    void b.offsetWidth;   // restart the beat
    b.classList.add("is-beat");
    toast(on ? data.i18n.favAdded : data.i18n.favRemoved);
  });
}

/** Repaints every ♥ on the page after a favorites change. */
export function repaintHearts(favs: Favorites, data: AlbumData): void {
  document.querySelectorAll<HTMLElement>("[data-fav-btn]").forEach((b) => paintHeart(b, favs.has(b.dataset.favBtn!), data));
}

// ------------------------------------------------------------------ favorites

const FAV_KEY = "cvw-album-favorites";

/** D14: favorite photo IDs, kept per browser. Works (for the visit) without localStorage. */
export class Favorites {
  private ids = new Set<string>();
  private listeners: (() => void)[] = [];

  constructor() {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      const list = raw ? (JSON.parse(raw) as unknown) : [];
      if (Array.isArray(list)) list.forEach((x) => typeof x === "string" && this.ids.add(x));
    } catch {
      /* private mode / blocked storage: in-memory only */
    }
  }

  has(id: string): boolean {
    return this.ids.has(id);
  }

  get count(): number {
    return this.ids.size;
  }

  list(): string[] {
    return Array.from(this.ids);
  }

  /** Returns the new state. */
  toggle(id: string): boolean {
    const on = !this.ids.has(id);
    if (on) this.ids.add(id);
    else this.ids.delete(id);
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(this.list()));
    } catch {
      /* ignore */
    }
    this.listeners.forEach((f) => f());
    return on;
  }

  onChange(f: () => void): void {
    this.listeners.push(f);
  }
}

// ---------------------------------------------------------------------- toast

let toastTimer = 0;

/** A short message; shown inside the open viewer / sheet when there is one (a modal dialog covers the page). */
export function toast(msg: string, ms = 2400): void {
  const el = document.querySelector<HTMLElement>("[data-album-toast]");
  if (!el || !msg) return;
  const host = document.querySelector<HTMLDialogElement>("dialog.album-viewer[open]") ?? document.querySelector<HTMLDialogElement>("dialog.album-sheet[open]") ?? document.body;
  if (el.parentElement !== host) host.appendChild(el);
  el.textContent = msg;
  el.classList.add("is-shown");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el.classList.remove("is-shown"), ms);
}
